import { useMemo } from "react"

interface GlowCalculations {
  haloBoxShadow: string
  hoverHaloBoxShadow: string
  activeHaloBoxShadow: string
}

export function useCTAGlow(isMobile: boolean, variant: "inline" | "tall-screen" | "mobile"): GlowCalculations {
  return useMemo(() => {
    if (variant === "mobile") {
      // Use exact same glow values as working mini-bet-button blue variant
      const outerRgb = "0,28,255"
      const innerRgb = "0,0,255"

      const OUTER_BLUR = 20
      const OUTER_SPREAD = 10
      const INNER_BLUR = 12
      const INNER_SPREAD = 4
      const alpha = 0.5

      const haloBoxShadow = `0 0 ${INNER_BLUR}px ${INNER_SPREAD}px rgba(${innerRgb},${alpha}), 0 0 ${OUTER_BLUR}px ${OUTER_SPREAD}px rgba(${outerRgb},${alpha})`

      // Hover and active glow variations (matching mini-bet-button)
      const hoverHaloBoxShadow = `0 0 ${INNER_BLUR * 1.4}px ${INNER_SPREAD * 1.3}px rgba(${innerRgb},${Math.min(1, alpha * 1.1)}), 0 0 ${OUTER_BLUR * 1.4}px ${OUTER_SPREAD * 1.3}px rgba(${outerRgb},${Math.min(1, alpha * 1.1)})`
      const activeHaloBoxShadow = `0 0 0px 0px rgba(${outerRgb},${alpha})`

      return {
        haloBoxShadow,
        hoverHaloBoxShadow,
        activeHaloBoxShadow,
      }
    }

    // Original logic for non-mobile variants
    const BLUR_BASE = isMobile ? 60 : 100
    const SPREAD_BASE = isMobile ? 20 : 30
    const GLOW_STRENGTH = isMobile ? 0.25 : 0.35

    // Static values for CTA (no scroll interactions)
    const sizeFactor = 1
    const haloOpacity = 0.8
    const directionFactor = 1

    // Calculate glow values (constant, no scroll reduction)
    const haloBlur = BLUR_BASE * sizeFactor * GLOW_STRENGTH * directionFactor
    const haloSpread = SPREAD_BASE * sizeFactor * GLOW_STRENGTH * directionFactor
    const scaledHaloOpacity = haloOpacity

    // Inner halo size relative to outer
    const INNER_GLOW_FACTOR = 0.25
    const innerBlur = haloBlur * INNER_GLOW_FACTOR
    const innerSpread = haloSpread * INNER_GLOW_FACTOR

    const outerBoxShadow = `0 0 ${haloBlur}px ${haloSpread}px rgba(0,28,255,${scaledHaloOpacity})`
    const innerBoxShadow = `0 0 ${innerBlur}px ${innerSpread}px rgba(0,28,255,${scaledHaloOpacity})`
    const haloBoxShadow = `${innerBoxShadow}, ${outerBoxShadow}`

    // Hover and active glow variations
    const HOVER_FACTOR = 1.0 // Use full glow strength like hero
    const ACTIVE_FACTOR = 0 // Completely collapse glow

    const hoverOuterBoxShadow = `0 0 ${haloBlur * HOVER_FACTOR}px ${haloSpread * HOVER_FACTOR}px rgba(0,28,255,${scaledHaloOpacity})`
    const hoverInnerBoxShadow = `0 0 ${innerBlur * HOVER_FACTOR}px ${innerSpread * HOVER_FACTOR}px rgba(0,28,255,${scaledHaloOpacity})`
    const hoverHaloBoxShadow = `${hoverInnerBoxShadow}, ${hoverOuterBoxShadow}`

    const activeOuterBoxShadow = `0 0 ${haloBlur * ACTIVE_FACTOR}px ${haloSpread * ACTIVE_FACTOR}px rgba(0,28,255,${scaledHaloOpacity})`
    const activeInnerBoxShadow = `0 0 ${innerBlur * ACTIVE_FACTOR}px ${innerSpread * ACTIVE_FACTOR}px rgba(0,28,255,${scaledHaloOpacity})`
    const activeHaloBoxShadow = `${activeInnerBoxShadow}, ${activeOuterBoxShadow}`

    return {
      haloBoxShadow,
      hoverHaloBoxShadow,
      activeHaloBoxShadow,
    }
  }, [isMobile, variant])
}
